/**
 * HomePage — city-aware editorial magazine.
 *
 * Rewritten from a TicketLeap-style organizer hero into a DesignMyNight-style
 * discovery magazine. The page detects the visitor's city from their IP
 * (server-side, silent — no permission prompt) and renders:
 *
 *   1. A magazine masthead with the city name + a city switcher dropdown.
 *   2. A featured editorial guide hero spread (1 big + 2 small).
 *   3. "Tonight in {City}" — the weekend events.
 *   4. The full editorial spread for the city — every published guide.
 *   5. Pill-filtered discovery feed (events · experiences · reservations · series).
 *   6. "By the mood" tiles wired to /events/{city}/tags/{slug}.
 *   7. A compact "list your spot" organizer stripe.
 *   8. Final CTA.
 *
 * Everything paints from a single /api/discovery/home call so the page
 * has no waterfalls. Each section degrades gracefully when its slice
 * is empty (collapses or shows a soft prompt instead of a blank hole).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Sparkles, ArrowRight, Calendar, MapPin, Compass, Utensils,
  Wallet, Zap, Code2, Star, TrendingUp, PartyPopper, Music, Wine,
  Mic2, Briefcase, Gift, ChevronRight, BookOpen, Newspaper, Clock,
  ArrowUpRight, Eye,
} from 'lucide-react';
import SEO from '../components/SEO';
import { organizationLD, websiteLD } from '../utils/structuredData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const CATEGORY_ICONS = {
  party: PartyPopper, festival: Music, concert: Music, music: Music,
  comedy: Mic2, brunch: Utensils, food: Utensils, tasting: Utensils,
  wine: Wine, networking: Briefcase, business: Briefcase, conference: Briefcase,
  holiday: Gift, gala: Star,
};
const iconFor = (c) => CATEGORY_ICONS[(c || '').toLowerCase()] || Sparkles;

const titleize = (s = '') =>
  String(s).replace(/[-_]/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
  } catch { return iso; }
};

// Build the canonical URL for any feed row, kind-aware.
const urlFor = (row, kind) => {
  const c = row.city || 'denver';
  const slug = row.slug || row.event_id || row.brand_id;
  if (kind === 'experiences')  return `/ex/${c}/${slug}`;
  if (kind === 'reservations') return `/res/${c}/${slug}`;
  if (kind === 'series')       return `/series/${row.slug || row.brand_id}`;
  return `/events/${c}/${slug}`;
};

// Current issue label for the masthead: "Issue · Mar 2026"
const issueLabel = () => {
  const d = new Date();
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

// ===========================================================================
// MAIN
// ===========================================================================
export default function HomePage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState(null);   // user-overridden city
  const [tab, setTab] = useState('all');

  // One fetch, full payload. Re-runs when the visitor picks a different city.
  useEffect(() => {
    let live = true;
    setLoading(true);
    const q = city ? `?city=${encodeURIComponent(city)}` : '';
    axios.get(`${API}/discovery/home${q}`)
      .then((r) => { if (live) { setData(r.data); setLoading(false); } })
      .catch(() => { if (live) { setData({}); setLoading(false); } });
    return () => { live = false; };
  }, [city]);

  const detectedCity = data?.detected_city || 'denver';
  const cities       = data?.available_cities || [];
  const editorial    = data?.editorial || [];
  const featuredEv   = data?.featured_events || [];
  const weekend      = data?.this_weekend || [];
  const week         = data?.this_week || [];
  const upcoming     = data?.upcoming || [];
  const experiences  = data?.experiences || [];
  const reservations = data?.reservations || [];
  const brands       = data?.brands || [];
  const moods        = data?.trending_categories || [];
  const spillover    = data?.nearby_other_cities || [];
  const stats        = data?.stats || {};

  // The feed under the magazine — pill-filtered.
  const feed = useMemo(() => {
    const tagged = [
      ...upcoming.map((r) => ({ ...r, _kind: 'events' })),
      ...experiences.map((r) => ({ ...r, _kind: 'experiences' })),
      ...reservations.map((r) => ({ ...r, _kind: 'reservations' })),
      ...brands.map((r) => ({ ...r, _kind: 'series' })),
    ];
    if (tab === 'all') return tagged.slice(0, 12);
    return tagged.filter((r) => r._kind === tab).slice(0, 12);
  }, [tab, upcoming, experiences, reservations, brands]);

  const cityLabel = titleize(detectedCity);

  return (
    <div className="min-h-screen bg-background" data-testid="home-page">
      <SEO
        title={`Eventism — Discover ${cityLabel}: events, experiences, dinners & guides`}
        description={`The city editorial: editor-picked things to do in ${cityLabel} — events tonight, experiences this week, dinner reservations, and curated city guides.`}
        canonical="/discover"
        jsonLd={[organizationLD(), websiteLD()]}
      />

      <Masthead
        city={detectedCity}
        cityLabel={cityLabel}
        cities={cities}
        onPickCity={setCity}
        stats={stats}
        geoSource={data?.geo_source}
      />

      {loading ? (
        <LoadingShell />
      ) : (
        <>
          <EditorialHero editorial={editorial} city={detectedCity} />

          <WeekendRow
            city={detectedCity}
            cityLabel={cityLabel}
            featured={featuredEv}
            weekend={weekend}
            week={week}
            spillover={spillover}
          />

          <MagazineSpread editorial={editorial} city={detectedCity} cityLabel={cityLabel} />

          <DiscoveryFeed
            tab={tab}
            setTab={setTab}
            feed={feed}
            counts={{
              all: upcoming.length + experiences.length + reservations.length + brands.length,
              events: upcoming.length,
              experiences: experiences.length,
              reservations: reservations.length,
              series: brands.length,
            }}
            cityLabel={cityLabel}
          />

          <MoodTiles moods={moods} city={detectedCity} cityLabel={cityLabel} />

          <OrganizerStripe onSell={() => navigate('/organizations/apply')} />

          <FinalCTA onSell={() => navigate('/organizations/apply')} />
        </>
      )}
    </div>
  );
}

// ===========================================================================
// 1. Masthead — editorial header
// ===========================================================================
function Masthead({ city, cityLabel, cities, onPickCity, stats, geoSource }) {
  return (
    <section
      className="relative overflow-hidden pt-20 pb-10 sm:pt-28 sm:pb-14 border-b border-border/50"
      data-testid="home-masthead"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,rgba(217,70,239,0.14),transparent_55%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_30%,rgba(245,158,11,0.10),transparent_55%)]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* top bar — issue · city switcher */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
            <Newspaper className="h-3.5 w-3.5 text-amber-300" />
            <span>The Eventism Edition</span>
            <span className="text-border">·</span>
            <span>Issue · {issueLabel()}</span>
            {geoSource === 'ip' && (
              <span className="text-emerald-400/80 normal-case tracking-normal">
                · Detected from your location
              </span>
            )}
          </div>

          <div className="flex items-center gap-2" data-testid="city-switcher">
            <MapPin className="h-3.5 w-3.5 text-fuchsia-300" />
            <Select value={city} onValueChange={onPickCity}>
              <SelectTrigger
                className="h-9 w-[180px] text-sm font-semibold border-border"
                data-testid="city-switcher-trigger"
              >
                <SelectValue placeholder="Pick a city" />
              </SelectTrigger>
              <SelectContent>
                {cities.length === 0 ? (
                  <SelectItem value={city}>{cityLabel}</SelectItem>
                ) : (
                  cities.map((c) => (
                    <SelectItem
                      key={c}
                      value={c}
                      data-testid={`city-opt-${c}`}
                    >
                      {titleize(c)}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* big masthead */}
        <div className="grid lg:grid-cols-[1.4fr_0.6fr] gap-10 items-end">
          <div className="space-y-5">
            <Badge
              variant="outline"
              className="border-fuchsia-400/30 text-fuchsia-200 bg-fuchsia-500/5 px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold rounded-full"
              data-testid="masthead-eyebrow"
            >
              <Sparkles className="h-3 w-3 mr-1.5 inline" />
              Tonight in {cityLabel}
            </Badge>
            <h1
              className="font-bold font-['Space_Grotesk'] tracking-tight leading-[0.92] text-5xl sm:text-6xl lg:text-7xl"
              data-testid="masthead-h1"
            >
              The {cityLabel}{' '}
              <span className="bg-gradient-to-r from-amber-300 via-fuchsia-300 to-indigo-300 bg-clip-text text-transparent">
                edit.
              </span>
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Editor-picked events, dinners, experiences, and the best
              spots in town — updated weekly, curated by humans, ranked
              by what locals actually book.
            </p>
          </div>

          {/* stats stack on the right — gives the masthead weight */}
          <div className="grid grid-cols-2 lg:grid-cols-1 gap-3" data-testid="masthead-stats">
            <StatChip
              icon={<Calendar className="h-4 w-4 text-fuchsia-300" />}
              label="Upcoming"
              value={String(stats.total_upcoming ?? 0)}
              sub="events on Eventism"
            />
            <StatChip
              icon={<BookOpen className="h-4 w-4 text-amber-300" />}
              label="City guides"
              value={String(stats.total_guides ?? 0)}
              sub="published & verified"
            />
            <StatChip
              icon={<MapPin className="h-4 w-4 text-emerald-300" />}
              label="Cities live"
              value={String(stats.cities_served ?? 0)}
              sub="and counting"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function StatChip({ icon, label, value, sub }) {
  return (
    <div className="p-3 rounded-xl border border-border bg-card/60 backdrop-blur-sm">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
        {icon} {label}
      </div>
      <p className="text-2xl font-bold font-['Space_Grotesk'] mt-1">{value}</p>
      <p className="text-[11px] text-muted-foreground">{sub}</p>
    </div>
  );
}

// ===========================================================================
// 2. Editorial Hero — big cover + 2 stacked picks
// ===========================================================================
function EditorialHero({ editorial, city }) {
  if (!editorial || editorial.length === 0) return null;
  const [lead, ...rest] = editorial;
  const secondary = rest.slice(0, 2);

  return (
    <section className="py-12 sm:py-16" data-testid="home-editorial-hero">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow="The cover"
          title="This week's lead story"
          right={
            <Link
              to={`/${city || 'denver'}/guides`}
              className="text-xs uppercase tracking-widest font-bold text-fuchsia-300 hover:text-fuchsia-200 inline-flex items-center"
              data-testid="editorial-see-all"
            >
              See all guides <ChevronRight className="h-4 w-4" />
            </Link>
          }
        />

        <div className="grid lg:grid-cols-[1.4fr_0.6fr] gap-6 mt-8">
          <EditorialLead guide={lead} />
          <div className="space-y-6">
            {secondary.map((g) => <EditorialSide key={g.slug} guide={g} />)}
            {secondary.length === 0 && (
              <div className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                More guides dropping this week.
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function EditorialLead({ guide }) {
  return (
    <Link
      to={`/${guide.city}/guides/${guide.slug}`}
      className="group relative block overflow-hidden rounded-2xl border border-border h-[420px] sm:h-[520px]"
      data-testid={`editorial-lead-${guide.slug}`}
    >
      {guide.cover_image ? (
        <img
          src={guide.cover_image}
          alt={guide.title}
          loading="eager"
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-500/30 via-amber-500/15 to-indigo-500/30" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
      <div className="absolute top-4 left-4 flex items-center gap-2">
        <Badge className="bg-fuchsia-500 hover:bg-fuchsia-500 text-white border-0 text-[10px] uppercase tracking-widest font-bold">
          City guide
        </Badge>
        {guide.category_label && (
          <Badge variant="outline" className="bg-black/40 backdrop-blur border-white/30 text-white text-[10px] uppercase tracking-widest font-bold">
            {guide.category_label}
          </Badge>
        )}
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 space-y-3">
        <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-amber-200/90">
          {titleize(guide.city)} · The editors&apos; pick
        </p>
        <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-['Space_Grotesk'] text-white leading-[0.95] tracking-tight">
          {guide.title}
        </h3>
        <p className="text-sm sm:text-base text-white/85 max-w-2xl line-clamp-2">
          {guide.meta_description}
        </p>
        <div className="flex items-center gap-4 text-[11px] uppercase tracking-widest text-white/70 font-bold pt-1">
          <span className="inline-flex items-center gap-1">
            <Eye className="h-3 w-3" /> {guide.view_count || 0} reads
          </span>
          <span className="inline-flex items-center gap-1">
            <ArrowUpRight className="h-3 w-3" /> Read guide
          </span>
        </div>
      </div>
    </Link>
  );
}

function EditorialSide({ guide }) {
  return (
    <Link
      to={`/${guide.city}/guides/${guide.slug}`}
      className="group relative block overflow-hidden rounded-2xl border border-border h-[245px]"
      data-testid={`editorial-side-${guide.slug}`}
    >
      {guide.cover_image ? (
        <img
          src={guide.cover_image}
          alt={guide.title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/30 to-fuchsia-500/30" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-4 space-y-1.5">
        <p className="text-[9px] uppercase tracking-[0.25em] font-bold text-amber-200/90">
          {guide.category_label || 'City guide'}
        </p>
        <h4 className="text-lg font-bold font-['Space_Grotesk'] text-white leading-tight line-clamp-2">
          {guide.title}
        </h4>
      </div>
    </Link>
  );
}

// ===========================================================================
// 3. Weekend row — "Tonight in {City}"
// ===========================================================================
function WeekendRow({ city, cityLabel, featured, weekend, week, spillover }) {
  // Composition priority: featured first, then weekend, then this_week, then spillover.
  const rows = [
    ...featured,
    ...weekend.filter((w) => !featured.find((f) => f.event_id === w.event_id)),
    ...week.filter((w) =>
      !featured.find((f) => f.event_id === w.event_id) &&
      !weekend.find((wk) => wk.event_id === w.event_id)
    ),
  ].slice(0, 6);

  const usingSpillover = rows.length === 0 && spillover.length > 0;
  const display = rows.length > 0 ? rows : spillover.slice(0, 6);

  return (
    <section
      className="py-12 sm:py-16 border-t border-border/40 bg-card/30"
      data-testid="home-weekend"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow={usingSpillover ? 'Around the country' : 'On the calendar'}
          title={usingSpillover
            ? `${cityLabel} is heating up — here's what's hot elsewhere`
            : `What's on in ${cityLabel} this weekend`}
          right={
            <Link
              to={`/find-events${city ? `?city=${city}` : ''}`}
              className="text-xs uppercase tracking-widest font-bold text-fuchsia-300 hover:text-fuchsia-200 inline-flex items-center"
              data-testid="weekend-see-all"
            >
              Full calendar <ChevronRight className="h-4 w-4" />
            </Link>
          }
        />

        {display.length === 0 ? (
          <Card className="border-dashed border-2 border-border mt-8">
            <CardContent className="p-10 text-center space-y-3" data-testid="weekend-empty">
              <Compass className="h-8 w-8 mx-auto text-fuchsia-300" />
              <p className="text-base font-semibold">
                Nothing on the calendar in {cityLabel} just yet.
              </p>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Be the first to list — Eventism takes 5 minutes to set up.
              </p>
              <Link to="/organizations/apply">
                <Button className="bg-gradient-to-r from-amber-500 via-fuchsia-500 to-indigo-500 hover:opacity-90 text-white font-bold border-0">
                  List your event <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8" data-testid="weekend-grid">
            {display.map((e, i) => (
              <EventBigCard key={e.event_id} ev={e} rank={i + 1} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function EventBigCard({ ev, rank }) {
  const Icon = iconFor(ev.category);
  return (
    <Link
      to={urlFor(ev, 'events')}
      className="group block"
      data-testid={`weekend-card-${ev.event_id}`}
    >
      <Card className="relative border-border hover:border-fuchsia-400/60 transition-all h-full overflow-hidden hover:translate-y-[-3px]">
        <div className="aspect-[4/3] bg-card overflow-hidden relative">
          {ev.image_url ? (
            <img
              src={ev.image_url}
              alt={ev.name}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-fuchsia-500/20 via-amber-500/10 to-indigo-500/20 flex items-center justify-center">
              <Icon className="h-12 w-12 text-fuchsia-200/70" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
          {rank && (
            <span className="absolute top-2 left-2 h-7 w-7 inline-flex items-center justify-center rounded-full bg-black/70 backdrop-blur text-white text-xs font-bold font-['Space_Grotesk']">
              {rank}
            </span>
          )}
          {ev.is_featured && (
            <Badge className="absolute top-2 right-2 bg-amber-500 text-black hover:bg-amber-500 border-0 text-[10px] uppercase tracking-widest font-bold">
              Featured
            </Badge>
          )}
          <div className="absolute bottom-0 left-0 right-0 p-3">
            <p className="text-xs uppercase tracking-widest font-bold text-amber-200/90">
              {formatDate(ev.event_date)} {ev.event_time ? `· ${ev.event_time.slice(0,5)}` : ''}
            </p>
            <p className="text-lg font-bold font-['Space_Grotesk'] text-white leading-tight line-clamp-2">
              {ev.name}
            </p>
          </div>
        </div>
        <CardContent className="p-3 space-y-1.5">
          {ev.tagline && (
            <p className="text-xs text-muted-foreground line-clamp-2">{ev.tagline}</p>
          )}
          <div className="flex items-center justify-between pt-1">
            <span className="text-[10px] uppercase font-bold tracking-widest text-muted-foreground">
              {titleize(ev.category || 'Event')}
              {ev.venue?.name && ` · ${ev.venue.name}`}
            </span>
            {ev.ticket_price_min != null && ev.ticket_price_min !== '' && Number(ev.ticket_price_min) > 0 && (
              <span className="text-xs font-bold text-amber-300">
                From ${Number(ev.ticket_price_min)}
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

// ===========================================================================
// 4. Magazine Spread — full editorial grid for the city
// ===========================================================================
function MagazineSpread({ editorial, city, cityLabel }) {
  if (!editorial || editorial.length <= 3) return null;
  const remainder = editorial.slice(3, 11);

  return (
    <section className="py-12 sm:py-16 border-t border-border/40" data-testid="home-spread">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow="The spread"
          title={`More from ${cityLabel}`}
          right={
            <Link
              to={`/${city || 'denver'}/guides`}
              className="text-xs uppercase tracking-widest font-bold text-fuchsia-300 hover:text-fuchsia-200 inline-flex items-center"
              data-testid="spread-see-all"
            >
              Browse all <ChevronRight className="h-4 w-4" />
            </Link>
          }
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          {remainder.map((g) => (
            <Link
              key={g.slug}
              to={`/${g.city}/guides/${g.slug}`}
              className="group block"
              data-testid={`spread-guide-${g.slug}`}
            >
              <Card className="border-border hover:border-amber-400/60 transition-all h-full overflow-hidden">
                <div className="aspect-[5/3] bg-card overflow-hidden relative">
                  {g.cover_image ? (
                    <img
                      src={g.cover_image}
                      alt={g.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-amber-500/20 to-fuchsia-500/20 flex items-center justify-center">
                      <BookOpen className="h-8 w-8 text-amber-300/70" />
                    </div>
                  )}
                </div>
                <CardContent className="p-3 space-y-1.5">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-amber-300">
                    {g.category_label || 'City guide'}
                  </p>
                  <p className="font-bold leading-tight group-hover:text-amber-200 transition-colors line-clamp-2">
                    {g.title}
                  </p>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {g.meta_description}
                  </p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// ===========================================================================
// 5. Discovery feed — pill filter row + kind-aware grid
// ===========================================================================
const PILLS = [
  { id: 'all',          label: 'All',          accent: 'fuchsia' },
  { id: 'events',       label: 'Events',       accent: 'fuchsia' },
  { id: 'experiences',  label: 'Experiences',  accent: 'amber'   },
  { id: 'reservations', label: 'Reservations', accent: 'emerald' },
  { id: 'series',       label: 'Clubs',        accent: 'indigo'  },
];

const ACCENT = {
  events:       { bar: 'bg-fuchsia-500',  label: 'Event'       },
  experiences:  { bar: 'bg-amber-500',    label: 'Experience'  },
  reservations: { bar: 'bg-emerald-500',  label: 'Reservation' },
  series:       { bar: 'bg-indigo-500',   label: 'Club'        },
};

function DiscoveryFeed({ tab, setTab, feed, counts, cityLabel }) {
  return (
    <section
      id="discovery"
      className="py-12 sm:py-16 border-t border-border/40 bg-card/30"
      data-testid="home-discovery"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow="Book tonight"
          title={`What's bookable in ${cityLabel}`}
          right={
            <Link
              to="/find-events"
              className="text-xs uppercase tracking-widest font-bold text-fuchsia-300 hover:text-fuchsia-200 inline-flex items-center"
              data-testid="discovery-see-all"
            >
              See full directory <ChevronRight className="h-4 w-4" />
            </Link>
          }
        />

        <div className="flex flex-wrap items-center gap-2 mt-6 mb-6" role="tablist" data-testid="kind-pill-row">
          {PILLS.map((k) => {
            const active = tab === k.id;
            return (
              <button
                key={k.id}
                onClick={() => setTab(k.id)}
                role="tab"
                aria-selected={active}
                data-testid={`pill-${k.id}`}
                className={`h-9 px-4 rounded-full text-sm font-semibold transition-all border ${
                  active
                    ? 'bg-foreground text-background border-foreground'
                    : 'border-border text-muted-foreground hover:text-foreground hover:border-fuchsia-400/50'
                }`}
              >
                {k.label}
                <span className={`ml-2 inline-flex items-center justify-center text-[10px] font-bold px-1.5 rounded-full ${
                  active ? 'bg-background/15 text-background' : 'bg-muted text-muted-foreground'
                }`}>
                  {counts[k.id] ?? 0}
                </span>
              </button>
            );
          })}
        </div>

        {feed.length === 0 ? (
          <Card className="border-dashed border-2 border-border" data-testid="feed-empty">
            <CardContent className="p-10 text-center space-y-3">
              <Compass className="h-8 w-8 mx-auto text-fuchsia-300" />
              <p className="text-base font-semibold">Nothing here yet — but it&apos;s brewing.</p>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Be one of the first organizers to claim the {cityLabel} catalog.
              </p>
              <Link to="/organizations/apply">
                <Button className="bg-gradient-to-r from-amber-500 via-fuchsia-500 to-indigo-500 hover:opacity-90 text-white font-bold border-0">
                  List your spot <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="feed-grid">
            {feed.map((r) => <FeedCard key={`${r._kind}-${r.event_id || r.brand_id}`} card={r} />)}
          </div>
        )}
      </div>
    </section>
  );
}

function FeedCard({ card }) {
  const Icon = iconFor(card.category);
  const accent = ACCENT[card._kind] || ACCENT.events;
  return (
    <Link
      to={urlFor(card, card._kind)}
      className="group block"
      data-testid={`feed-card-${card._kind}-${card.event_id || card.brand_id}`}
    >
      <Card className="relative border-border hover:border-fuchsia-400/60 transition-colors h-full overflow-hidden">
        <span className={`absolute top-0 left-0 right-0 h-0.5 ${accent.bar}`} />
        <div className="aspect-video bg-card overflow-hidden relative">
          {(card.image_url || card.cover_url) ? (
            <img
              src={card.image_url || card.cover_url}
              alt={card.name}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-fuchsia-500/15 via-amber-500/10 to-indigo-500/15 flex items-center justify-center">
              <Icon className="h-10 w-10 text-fuchsia-300/70" />
            </div>
          )}
          <Badge className={`absolute top-2 left-2 text-[10px] font-bold border-0 ${accent.bar} text-white`}>
            {accent.label}
          </Badge>
        </div>
        <CardContent className="p-3 space-y-1.5">
          <p className="font-bold truncate group-hover:text-fuchsia-300 transition-colors">{card.name}</p>
          <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
            {card.event_date && (
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3 text-fuchsia-400" />
                {formatDate(card.event_date)}
              </span>
            )}
            {card.city && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" /> {titleize(card.city)}
              </span>
            )}
          </div>
          {card.tagline && (
            <p className="text-xs text-muted-foreground line-clamp-2">{card.tagline}</p>
          )}
          <div className="flex items-center justify-between pt-1">
            <span className="text-[10px] uppercase font-bold tracking-wide text-muted-foreground">
              {titleize(card.category || accent.label)}
            </span>
            {card.ticket_price_min != null && card.ticket_price_min !== '' && Number(card.ticket_price_min) > 0 && (
              <span className="text-xs font-semibold text-amber-300">
                From ${Number(card.ticket_price_min)}
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

// ===========================================================================
// 6. Mood tiles — categories → tag pages
// ===========================================================================
function MoodTiles({ moods, city, cityLabel }) {
  if (!moods || moods.length === 0) return null;

  return (
    <section className="py-12 sm:py-16 border-t border-border/40" data-testid="home-moods">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow="By the mood"
          title={`How are you feeling, ${cityLabel}?`}
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-8">
          {moods.map((m) => {
            const Icon = iconFor(m.slug);
            return (
              <Link
                key={m.slug}
                to={`/events/${city}/tags/${m.slug}`}
                className="group relative block aspect-[4/3] overflow-hidden rounded-xl border border-border hover:border-fuchsia-400/60 transition-all"
                data-testid={`mood-${m.slug}`}
              >
                {m.image_url ? (
                  <img
                    src={m.image_url}
                    alt={m.label}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
                  />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-500/30 via-amber-500/15 to-indigo-500/30" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                <div className="absolute inset-0 p-4 flex flex-col justify-between">
                  <div className="self-start h-8 w-8 rounded-full bg-white/15 backdrop-blur flex items-center justify-center">
                    <Icon className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-base font-bold font-['Space_Grotesk'] text-white">{m.label}</p>
                    <p className="text-[11px] uppercase tracking-widest text-white/80 font-bold">
                      {m.count} {m.count === 1 ? 'event' : 'events'}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ===========================================================================
// 7. Organizer stripe — compact pitch tucked under the magazine
// ===========================================================================
function OrganizerStripe({ onSell }) {
  return (
    <section className="py-16 border-t border-border/40 bg-card/30" data-testid="home-organizer-stripe">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card className="border-fuchsia-500/30 bg-gradient-to-br from-fuchsia-500/10 via-amber-500/5 to-indigo-500/10 overflow-hidden">
          <CardContent className="p-8 sm:p-10 grid lg:grid-cols-[1.4fr_1fr] gap-8 items-center">
            <div className="space-y-4">
              <Badge variant="outline" className="border-amber-400/30 bg-amber-500/5 text-amber-200 text-[10px] uppercase tracking-widest font-bold">
                <Sparkles className="h-3 w-3 mr-1.5 inline" />
                Run a venue, a club, or a tour?
              </Badge>
              <h3 className="text-2xl sm:text-3xl font-bold font-['Space_Grotesk'] tracking-tight">
                Get listed in the edition. Sell tickets the same day.
              </h3>
              <p className="text-sm text-muted-foreground max-w-lg leading-relaxed">
                Eventism is built for organizers. Stripe payouts, AI listing
                writer, embeddable widget, and editorial placement — all from one dashboard.
              </p>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5"><Wallet className="h-3.5 w-3.5 text-emerald-400" /> No monthly fee</span>
                <span className="inline-flex items-center gap-1.5"><Zap className="h-3.5 w-3.5 text-emerald-400" /> 5-minute setup</span>
                <span className="inline-flex items-center gap-1.5"><Code2 className="h-3.5 w-3.5 text-emerald-400" /> Sells on your site</span>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <Button
                onClick={onSell}
                size="lg"
                className="h-12 px-7 bg-gradient-to-r from-amber-500 via-fuchsia-500 to-indigo-500 hover:opacity-90 text-white font-bold border-0"
                data-testid="organizer-stripe-cta"
              >
                Start selling free <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Link to="/find-events" className="text-xs text-center text-muted-foreground hover:text-foreground">
                Or keep browsing the edition →
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

// ===========================================================================
// 8. Final CTA — small two-button band
// ===========================================================================
function FinalCTA({ onSell }) {
  return (
    <section className="py-20 border-t border-border/40 relative overflow-hidden" data-testid="home-final-cta">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(217,70,239,0.12),transparent_60%)]" />
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-5">
        <h2 className="text-3xl sm:text-4xl font-bold font-['Space_Grotesk'] tracking-tight leading-tight">
          The city, edited.{' '}
          <span className="bg-gradient-to-r from-amber-300 via-fuchsia-300 to-indigo-300 bg-clip-text text-transparent">
            All in one place.
          </span>
        </h2>
        <p className="text-sm sm:text-base text-muted-foreground">
          Discover tonight, book in two taps, or list your own spot — Eventism
          is the city operating system for events, experiences, and reservations.
        </p>
        <div className="flex flex-wrap justify-center gap-3 pt-2">
          <Link to="/find-events">
            <Button size="lg" variant="outline" className="h-12 px-7 border-border" data-testid="final-browse-btn">
              Browse the edition
            </Button>
          </Link>
          <Button
            onClick={onSell}
            size="lg"
            className="h-12 px-7 bg-gradient-to-r from-amber-500 via-fuchsia-500 to-indigo-500 hover:opacity-90 text-white font-bold border-0"
            data-testid="final-sell-btn"
          >
            List your spot <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </section>
  );
}

// ===========================================================================
// Shared bits
// ===========================================================================
function SectionHeader({ eyebrow, title, right }) {
  return (
    <div className="flex items-end justify-between flex-wrap gap-4">
      <div>
        <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-amber-300 mb-2">
          {eyebrow}
        </p>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-['Space_Grotesk'] tracking-tight leading-tight">
          {title}
        </h2>
      </div>
      {right}
    </div>
  );
}

function LoadingShell() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="home-loading">
      <div className="grid lg:grid-cols-[1.4fr_0.6fr] gap-6">
        <div className="h-[420px] sm:h-[520px] rounded-2xl bg-muted/30 animate-pulse" />
        <div className="space-y-6">
          <div className="h-[245px] rounded-2xl bg-muted/30 animate-pulse" />
          <div className="h-[245px] rounded-2xl bg-muted/30 animate-pulse" />
        </div>
      </div>
    </div>
  );
}
